import { AuthNavParams } from '@euler/app/flows/auth/Auth';
import { useAuthTheme } from '@euler/app/flows/auth/hooks';
import { Avatar } from '@euler/components/Avatar';
import { Button } from '@euler/components/Button';
import { Colors } from '@euler/components/Colors';
import { FormGroup, FormInput } from '@euler/components/form';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AppleSignInAuthorizationInfo } from '@euler/functions/useAppleSignIn';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { MpUserInfo } from '@euler/model';
import { PredefinedVerifyCodeScene } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { isMobilePhone } from '@euler/utils/validators';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { nanoid } from 'nanoid/non-secure';
import { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import Toast from 'react-native-root-toast';
import { LoginHeader } from '../components/LoginHeader';
import { LoginScreenView } from '../components/LoginScreenView';

const kCodeLength = 6;
const kAvatarSize = 80;
const kAvatarBorder = 5;

type Props = {
  title?: string;
  message?: string;
  provider?:
    | {
        type: 'phone';
      }
    | { type: 'weixin'; nonce: string; userInfo: MpUserInfo }
    | { type: 'appleSignIn'; authorizationInfo: AppleSignInAuthorizationInfo };
};

export const LoginPhoneScreen = wrapNavigatorScreen(
  ({ title, message, provider }: Props) => {
    const theme = useAuthTheme();
    const { rpx } = useResponsiveContext();
    const navigation = useNavigation<StackNavigationProp<AuthNavParams>>();
    const loading = useAppLoading();

    const { smsService } = useServiceFactory();

    const [mobile, setMobile] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [ticket, setTicket] = useState<string>();

    const nonce = useRef(nanoid()).current;

    useEffect(() => {
      smsService
        .requestVerifyCodeTicket(nonce)
        .then(result => {
          setTicket(result);
        })
        .catch(onErrorIgnore);
    }, [nonce, smsService]);

    const onSendCode = usePersistFn(
      async (
        callback?: (
          error: Error | null | undefined,
          result?: {
            ticket: string;
            length: number;
            ttl: number;
          },
        ) => void,
      ) => {
        if (!ticket) return;

        loading.show();
        setIsLoading(true);

        try {
          const ttl = await smsService.sendVerifyCode(
            ticket,
            mobile,
            PredefinedVerifyCodeScene.Login,
            kCodeLength,
          );
          setIsLoading(false);
          loading.hide();
          Toast.show('验证码已发送', {
            duration: 300,
          });
          if (callback) {
            callback(null, { ticket, length: kCodeLength, ttl });
          } else {
            navigation.push('_LoginCode', {
              mobile,
              provider,
              codeInfo: {
                ticket,
                length: kCodeLength,
                ttl,
              },
              onSendCode,
            });
          }
        } catch (e) {
          setIsLoading(false);
          loading.hide();
          Toast.show('发送失败: ' + (e as Error).message, {
            duration: 3000,
          });
          if (callback) {
            callback(e as Error);
          }
        }
      },
    );

    const onNext = usePersistFn(() => {
      if (isLoading) return;
      if (!ticket) {
        Toast.show('发送失败: E_TICKET', {
          duration: 1500,
        });
        return;
      }
      onSendCode().catch(onErrorIgnore);
    });

    const disabled = !isMobilePhone(mobile);

    return (
      <LoginScreenView>
        <View
          css={`
            padding: ${rpx(18)}px ${rpx(32)}px;
            align-self: stretch;
            flex: 1;
          `}
        >
          <LoginHeader
            title={
              title ??
              (provider?.type === 'weixin' || provider?.type === 'appleSignIn'
                ? '绑定手机号'
                : '验证码登录')
            }
            subTitle={message ?? '输入手机号并接收登录验证码'}
          />
          {provider?.type === 'weixin' ? (
            <View
              css={`
                width: ${rpx(kAvatarSize + 2 * kAvatarBorder)}px;
                height: ${rpx(kAvatarSize + 2 * kAvatarBorder)}px;
                background-color: ${theme.avatarBgColor};
                border-color: ${Colors.Gray9};
                border-width: ${rpx(kAvatarBorder)}px;
                border-radius: ${rpx(kAvatarSize + 2 * kAvatarBorder) / 2}px;
                align-self: center;
                align-items: center;
                justify-content: center;
                margin-top: ${rpx(8)}px;
                margin-bottom: ${rpx(16)}px;
              `}
            >
              <Avatar
                uri={provider.userInfo.headimgurl}
                name={provider.userInfo.nickname ?? ''}
                css={`
                  width: ${rpx(kAvatarSize)}px;
                  height: ${rpx(kAvatarSize)}px;
                  border-radius: ${rpx(kAvatarSize) / 2}px;
                `}
              />
            </View>
          ) : null}
          <View
            css={`
              margin-bottom: ${rpx(16)}px;
            `}
          >
            <FormGroup>
              <FormInput
                placeholder="手机号"
                value={mobile}
                onChange={setMobile}
                keyboardType="number-pad"
                returnKeyType="next"
                returnKeyLabel="继续"
                autoFocus
                bgColor={theme.textInput.bgColor}
                placeholderTextColor={theme.textInput.placeholderTextColor}
                css={`
                  color: ${theme.textInput.textColor};
                  font-size: ${rpx(16)}px;
                  font-family: ${FontFamily.NotoSans.Medium};
                  letter-spacing: ${rpx(1)}px;
                  font-size: ${rpx(14)}px;
                  padding: 0 ${rpx(22)}px;
                `}
              />
            </FormGroup>
          </View>
        </View>
        <Button
          title="获取验证码"
          disabled={disabled}
          onPress={onNext}
          loading={isLoading}
          textStyle={{
            fontSize: rpx(18),
            lineHeight: rpx(32),
            color: disabled
              ? theme.disabledButtonTextColor
              : theme.buttonTextColor,
          }}
          css={`
            background-color: ${disabled
              ? theme.disabledButtonBgColor
              : theme.buttonBgColor};
            margin-bottom: ${rpx(32)}px;
            margin-left: ${rpx(32)}px;
            margin-right: ${rpx(32)}px;
            height: ${rpx(50)}px;
            border-radius: ${rpx(50) / 2}px;
          `}
        />
      </LoginScreenView>
    );
  },
);
